import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, RouteComponentProps } from 'react-router-dom';
import { noIndexNoFollowContent } from '@nykaa/seo/constants';
import { isValidObject } from '@nykaa/utils/validations';
import styled from 'App/styled';

import Error from 'shared/components/Error';
import BreadCrumb from 'components/Breadcrumb';
import ProTag from 'shared/components/Icons/ProTag';
import appEvents from 'store/app/actionTypes';
import { fetchDynamicFooterLinks } from 'shared/store/product/action';
import PageTypes from 'constants/pageTypes';
import { handleSkuIdInUrl } from 'store/product/helpers';
import { ProductLoader } from 'components/ProductLoader';
import { CAB_SECTION_ID } from 'shared/constants/productPage';
import { AB_FOOTER_LINK } from 'constants/product';
import { getEligibleForVariant } from 'helpers/experimentV2';
import { State } from 'store/types';
import {
  selectCurrentSku,
  selectIsFetchingError,
  selectIsProductLoading,
  selectVariants,
} from 'store/product/selector';
import { MatchProps } from 'shared/types/product';
import { toShowProTag } from 'helpers/utils/product/toShowProTag';
import MetaInfo from './MetaInfo';
import Info from './ProductInfo';
import MediaAndShare from './Media';
import CABList from './CustomerAlsoBought';
import Banners from './Banners';
import ProductDetails from './ProductDetails';
import { pushData } from '@nykaa/data-layer/utils';
import { getCookie } from '@nykaa/utils/cookies';

interface ProductProps extends RouteComponentProps<MatchProps> {
  productUnMount: () => { type: string };
  pdpCustomVideoEnabled: boolean;
  primaryCategories: any;
  isSearchable?: boolean;
}
const Wrapper = styled.div`
  max-width: 1200px !important;
  width: 90%;
  padding: 0 30px;
  margin: 0 auto;
  @media (max-width: 1024px) {
    padding: 0 15px !important;
    margin: 0;
    width: 100%;
  }
`;
const ProductDesc = styled.div`
  display: flex;
  max-width: 1200px !important;
  min-height: 450px;
  margin-bottom: ${({ theme }) => theme.spacing.spacing60};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 2px 3px 0 rgb(0 0 0 / 10%);
  justify-content: center;
  overflow: hidden;
`;

const Title = styled.div`
  overflow-x: hidden;
  flex: 1;
  position: relative;
  @media (min-width: 768px) and (max-width: 1024px) {
    flex: 2;
  }
`;

const ProductView = ({
  productUnMount,
  match,
  pdpCustomVideoEnabled,
  primaryCategories,
  isSearchable,
}: ProductProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [showCAB, setShowCAB] = useState(false);
  const variants = useSelector((state: State) => selectVariants(state));
  const currentSku = useSelector((state: State) => selectCurrentSku(state));
  const fetchingError = useSelector((state: State) =>
    selectIsFetchingError(state)
  );
  const loading = useSelector((state: State) => selectIsProductLoading(state));
  const parentId =
    useSelector((state: State) => state.productPage?.product?.id) || '';
  const proFlag =
    useSelector((state: State) => state.productPage?.product?.proFlag) || 0;
  const isPro = useSelector((state: State) => state.auth.user.isPro);
  const isDynamicFooterLinkEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, AB_FOOTER_LINK, 'A')
  );
  const { id } = match.params;

  useEffect(() => {
    if (isValidObject(primaryCategories) && isDynamicFooterLinkEnabled) {
      const l1Id = primaryCategories?.l1.id;
      const l2Id = primaryCategories?.l2.id;
      const l3Id = primaryCategories?.l3.id;
      if (l1Id && l2Id && l3Id) {
        fetchDynamicFooterLinks({ l1Id, l2Id, l3Id })(dispatch);
      }
    }
  }, [primaryCategories, dispatch, isDynamicFooterLinkEnabled]);

  useEffect(() => {
    if (currentSku) {
      window.requestIdleCallback(() => {
        dispatch({
          type: appEvents.APP_REDUCER_SET_PAGE,
          payload: PageTypes.PRODUCT,
        });
        handleSkuIdInUrl(history, currentSku, parentId);
      });
    }
    pushData({ pinCode: getCookie('pinCodeDel').toString() || '' });
  }, [currentSku, parentId, history, dispatch]);

  useEffect(() => {
    if (currentSku) {
      const {
        isPrEnabled = false,
        isEarlyAccess = false,
        isFlashSale = false,
      } = currentSku;
      pushData({
        saleConfig: {
          isPrEnabled,
          isEarlyAccess,
          isFlashSale,
        },
      });
    }
  }, [currentSku]);

  useEffect(() => {
    const data: { [key: string]: string } = {};
    if (variants && variants.length > 0) {
      const inStockVariants = variants.filter((x) => x.inStock);
      data.variantsCount = variants.length;
      data.inStockVariantCount = inStockVariants.length;
      data.oosVariantCount = variants.length - inStockVariants.length;
    }
    pushData(data);
  }, [variants]);

  useEffect(
    () => () => {
      productUnMount();
    },
    [productUnMount, id]
  );

  if (loading) {
    return <ProductLoader />;
  }

  if (fetchingError || !currentSku) {
    return <Error />;
  }

  const handleShowCAB = () => {
    setShowCAB(true);
    const CAB_SECTION = document?.getElementById(CAB_SECTION_ID);
    if (CAB_SECTION) {
      CAB_SECTION.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const showProTag = toShowProTag(isPro, proFlag);
  const metaRobotsValue = !isSearchable ? noIndexNoFollowContent : '';
  return (
    <>
      <Banners productId={parentId} />
      <Wrapper>
        <MetaInfo
          id={id}
          page={PageTypes.PRODUCT}
          metaRobots={metaRobotsValue}
        />
        <BreadCrumb />
        <ProductDesc>
          <MediaAndShare
            pdpCustomVideoEnabled={pdpCustomVideoEnabled}
            primaryCategories={primaryCategories}
          />
          <Title>
            {showProTag && <ProTag />}
            <Info handleShowCAB={handleShowCAB} />
          </Title>
        </ProductDesc>
        {showCAB && <CABList productId={parentId} />}
        <ProductDetails
          parentId={parentId}
          handleShowCAB={handleShowCAB}
        />
      </Wrapper>
    </>
  );
};

export default ProductView;
